import emailIcon from '@iconify/icons-carbon/email';
import locationIcon from '@iconify/icons-carbon/location';
import phoneVoice from '@iconify/icons-carbon/phone-voice';
import clock from '@iconify/icons-material-symbols/nest-clock-farsight-analog-outline-rounded';
import { Box, Container, Divider, Grid, Link, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Trans, useTranslation } from 'next-i18next';

import {  TextIconLabel } from '@larvitu/shared/mui/ui-components';
import { IconifyIcon } from '@larvitu/shared/react/components/ui-iconify-icon';

import type { FC, ReactNode } from 'react';

export interface FooterProps {
  siteBrand: ReactNode;
  openingHours?: Array<{
    day: string;
    time: string;
  }>;
  fax?: string;
  email?: string;
  address?: string;
  phone?: string;
  googleMapHref?: string;
  other?: ReactNode;
}

// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
const RootStyle = styled( 'footer' )(({ theme }) => ({
  backgroundColor: '#FBF7F3',
  paddingTop: '4rem',
}));

export const Footer: FC<FooterProps> = ({
  siteBrand: SiteBrand = void '0',
  email,
  address,
  phone,
  openingHours = [],
  fax = '',
  other = '',
  googleMapHref = '',
}) => {
  const { t } = useTranslation( 'common' );

  const footerOpeningHours: Array<{
    day: string[];
    time: string;
  }> = [];

  openingHours.forEach( (item) => {
    if (footerOpeningHours.some((element) => element.time === item.time)) {
      footerOpeningHours.forEach( (accItem) => {
        if (item.time === accItem.time) {
          accItem.day.push( item.day );
        }
      } );
    } else {
      footerOpeningHours.push( { day: [`${item.day}`], time: item.time } );
    }
  }, [] );

  return (
    <RootStyle>
      <Container>
        {other}
        <Grid container columns={6} alignItems="center">
          <Grid item xs={6}>
            <Stack
              direction={{
                xs: 'column',
                md: 'row',
              }}
              spacing={0.5}
              sx={{
                py: 3,
              }}
            >
              {SiteBrand}
            </Stack>
          </Grid>
          <Grid item xs={6}>
            <Box display="flex" justifyContent="center" mt={3}>
              <Grid spacing={0} rowGap={2} container>
                {(phone ?? '') && (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    lg={4}
                    order={{
                      xs: 3,
                      sm: 1,
                    }}
                  >
                    <Stack direction="column">
                      <TextIconLabel
                        icon={
                          <IconifyIcon
                            icon={phoneVoice}
                            width={24}
                            height={24}
                            style={{
                              marginRight: '8px',
                            }}
                          />
                        }
                        value={
                          <Typography>
                            Tel:
                          </Typography>
                        }
                      />
                      <Link
                        target="_self"
                        href={`tel:${phone ?? ''}`}
                        variant="subtitle2"
                        color="text.secondary"
                      >
                        {phone}
                      </Link>
                    </Stack>
                  </Grid>
                )}
                {(email ?? '') && (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    lg={4}
                    order={{
                      xs: 3,
                      sm: 2,
                    }}
                  >
                    <Stack direction="column">
                      <TextIconLabel
                        icon={
                          <IconifyIcon
                            icon={emailIcon}
                            width={24}
                            height={24}
                            style={{
                              marginRight: '8px',
                            }}
                          />
                        }
                        value={
                          <Typography>
                            E-Mail:
                          </Typography>
                        }
                      />
                      <Link
                        target="_self"
                        href={`mailto:${email ?? ''}`}
                        variant="subtitle2"
                        color="text.secondary"
                      >
                        {email?.split( ',' )[0].trim()}
                      </Link>
                    </Stack>
                  </Grid>
                )}
                {openingHours.length > 0 && (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    lg={4}
                    order={{
                      xs: 5,
                      sm: 5,
                      lg: 3,
                    }}
                  >
                    <Stack direction="column">
                      <TextIconLabel
                        icon={
                          <IconifyIcon
                            icon={clock}
                            width={24}
                            height={24}
                            style={{
                              marginRight: '8px',
                            }}
                          />
                        }
                        value={
                          <Typography>
                            Öffnungszeiten:
                          </Typography>
                        }
                      />
                      <>
                        <Typography variant="subtitle2" color="text.secondary">
                          <Grid container>
                            {footerOpeningHours.map((opening, index, array) => (
                              <Grid item key={index} xs={12}>
                                <Grid
                                  container
                                  alignItems="center"
                                  color="text.secondary"
                                  columnSpacing={3}
                                >
                                  <Grid item xs={12} sm={6}>
                                    <Typography
                                      component="span"
                                      variant="subtitle2"
                                    >
                                      {opening.day.map(
                                        (day, dayIndex, dayArray) => {
                                          if (dayIndex === dayArray.length - 1) {
                                            return day.replace( ':', '' );
                                          }

                                          return day.replace( ':', ',\n' );
                                        },
                                      )}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                    <Typography
                                      component="span"
                                      variant="subtitle2"
                                    >
                                      <Trans
                                        components={{ wbr: <wbr /> }}
                                        t={t}
                                        // t={i18n?.t}
                                      >
                                        {opening.time}
                                      </Trans>
                                    </Typography>
                                  </Grid>
                                </Grid>
                                {array.length - 1 > index && <Box component={Divider} mt={1} />}
                              </Grid>
                            ))}
                          </Grid>
                        </Typography>
                      </>
                    </Stack>
                  </Grid>
                )}
                {fax && (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    lg={4}
                    order={{
                      xs: 2,
                      sm: 2,
                      lg: 4,
                    }}
                  >
                    <Stack direction="column">
                      <TextIconLabel
                        icon={
                          <IconifyIcon
                            icon={clock}
                            width={24}
                            height={24}
                            style={{
                              marginRight: '8px',
                            }}
                          />
                        }
                        value={
                          <Typography>
                            Fax:
                          </Typography>
                        }
                      />
                      <Typography variant="subtitle2" color="text.secondary">
                        {fax}
                      </Typography>
                    </Stack>
                  </Grid>
                )}
                {(address ?? '') && (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    lg={4}
                    order={{
                      xs: 4,
                      sm: 4,
                      lg: 5,
                    }}
                  >
                    <Stack direction="column">
                      <TextIconLabel
                        icon={
                          <IconifyIcon
                            icon={locationIcon}
                            width={24}
                            height={24}
                            style={{
                              marginRight: '8px',
                            }}
                          />
                        }
                        value={
                          <Typography>
                            Adresse:
                          </Typography>
                        }
                      />
                      <Link
                        href={googleMapHref}
                        variant="subtitle2"
                        color="text.secondary"
                      >
                        {address}
                      </Link>
                    </Stack>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Grid>
          <Grid container alignItems="center" pt={2}>
            <Stack
              width="100%"
              direction={{
                xs: 'column',
                md: 'row',
              }}
              spacing={2.5}
              justifyContent="space-between"
              sx={{
                py: 3,
                px: 2,
                textAlign: 'center',
              }}
            >
              <Typography variant="body3" sx={{ color: 'text.secondary' }}>
                © {new Date().getFullYear()}. All rights reserved
              </Typography>
              <Stack direction="row" spacing={3} justifyContent="center">
                <Link
                  href="/impressum"
                  variant="body3"
                  sx={{ color: 'text.secondary' }}
                >
                  {t( 'company', { ns: 'common', defaultValue: '' } )}
                </Link>
                <Link
                  href="/datenschutz"
                  variant="body3"
                  sx={{ color: 'text.secondary' }}
                >
                  {t( 'data-privacy', { ns: 'common', defaultValue: '' } )}
                </Link>
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </RootStyle>
  );
};
